import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Plus, Settings, DollarSign, User, Calendar, MoreHorizontal, Trash2, Edit2, Trophy, XCircle } from 'lucide-react'
import { toast } from 'sonner'
import { useNavigate } from 'react-router-dom'
import { pipelineApi, dealApi, usersApi } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import type { Deal, CreateDealRequest } from '@/types'
import { format } from 'date-fns'
import { usePermissions } from '@/contexts/PermissionContext'

export default function DealsKanbanPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  usePermissions() // Hook called for context, permissions checked inline

  // State
  const [selectedPipelineId, setSelectedPipelineId] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  const [assignedToFilter, setAssignedToFilter] = useState<string>('all')
  const [draggedDeal, setDraggedDeal] = useState<Deal | null>(null)
  const [dragOverStageId, setDragOverStageId] = useState<string | null>(null)

  // Quick add deal state
  const [quickAddDialogOpen, setQuickAddDialogOpen] = useState(false)
  const [quickAddStageId, setQuickAddStageId] = useState<string>('')
  const [quickAddForm, setQuickAddForm] = useState<CreateDealRequest>({
    pipeline_id: '',
    deal_name: '',
    deal_value: 0,
  })

  // Won/Lost dialog state
  const [wonLostDialogOpen, setWonLostDialogOpen] = useState(false)
  const [wonLostDeal, setWonLostDeal] = useState<Deal | null>(null)
  const [wonLostType, setWonLostType] = useState<'won' | 'lost'>('won')
  const [wonLostReason, setWonLostReason] = useState('')

  // Queries
  const { data: pipelines = [] } = useQuery({
    queryKey: ['pipelines'],
    queryFn: () => pipelineApi.getPipelines(true),
  })

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.getAll(),
  })

  // Set default pipeline
  useEffect(() => {
    if (pipelines.length > 0 && !selectedPipelineId) {
      const defaultPipeline = pipelines.find(p => p.is_default) || pipelines[0]
      setSelectedPipelineId(defaultPipeline.pipeline_id)
    }
  }, [pipelines, selectedPipelineId])

  const { data: selectedPipeline } = useQuery({
    queryKey: ['pipeline', selectedPipelineId],
    queryFn: () => pipelineApi.getPipeline(selectedPipelineId),
    enabled: !!selectedPipelineId,
  })

  const { data: dealsResponse } = useQuery({
    queryKey: ['deals', selectedPipelineId, searchQuery, assignedToFilter],
    queryFn: () => dealApi.getDeals({
      pipeline_id: selectedPipelineId,
      search: searchQuery || undefined,
      assigned_to: assignedToFilter && assignedToFilter !== 'all' ? assignedToFilter : undefined,
      page_size: 1000, // Get all deals for kanban
    }),
    enabled: !!selectedPipelineId,
  })

  const deals = dealsResponse?.deals || []

  // Mutations
  const moveDealMutation = useMutation({
    mutationFn: ({ dealId, stageId, position }: { dealId: string; stageId: string; position?: number }) =>
      dealApi.moveDeal(dealId, { stage_id: stageId, position }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] })
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('messages.updateError'))),
  })

  const createDealMutation = useMutation({
    mutationFn: (data: CreateDealRequest) => dealApi.createDeal(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] })
      toast.success(t('messages.createSuccess'))
      setQuickAddDialogOpen(false)
      setQuickAddForm({ pipeline_id: '', deal_name: '', deal_value: 0 })
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('messages.createError'))),
  })

  const deleteDealMutation = useMutation({
    mutationFn: (dealId: string) => dealApi.deleteDeal(dealId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] })
      toast.success(t('messages.deleteSuccess'))
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('messages.deleteError'))),
  })

  const markWonMutation = useMutation({
    mutationFn: ({ dealId, reason }: { dealId: string; reason?: string }) =>
      dealApi.markWon(dealId, { won_reason: reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] })
      toast.success(t('crm.dealMarkedWon', 'Deal marked as won!'))
      setWonLostDialogOpen(false)
      setWonLostReason('')
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('messages.updateError'))),
  })

  const markLostMutation = useMutation({
    mutationFn: ({ dealId, reason }: { dealId: string; reason?: string }) =>
      dealApi.markLost(dealId, { lost_reason: reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] })
      toast.success(t('crm.dealMarkedLost', 'Deal marked as lost'))
      setWonLostDialogOpen(false)
      setWonLostReason('')
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('messages.updateError'))),
  })

  // Drag handlers
  const handleDragStart = (e: React.DragEvent, deal: Deal) => {
    setDraggedDeal(deal)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', deal.deal_id)
  }

  const handleDragOver = (e: React.DragEvent, stageId: string) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOverStageId(stageId)
  }

  const handleDragLeave = () => {
    setDragOverStageId(null)
  }

  const handleDrop = (e: React.DragEvent, targetStageId: string) => {
    e.preventDefault()
    setDragOverStageId(null)

    if (!draggedDeal) return
    if (draggedDeal.stage_id === targetStageId) {
      setDraggedDeal(null)
      return
    }

    moveDealMutation.mutate({
      dealId: draggedDeal.deal_id,
      stageId: targetStageId,
    })

    setDraggedDeal(null)
  }

  const handleDragEnd = () => {
    setDraggedDeal(null)
    setDragOverStageId(null)
  }

  // Quick add handlers
  const handleQuickAdd = (stageId: string) => {
    setQuickAddStageId(stageId)
    setQuickAddForm({
      pipeline_id: selectedPipelineId,
      stage_id: stageId,
      deal_name: '',
      deal_value: 0,
    })
    setQuickAddDialogOpen(true)
  }

  const handleQuickAddSubmit = () => {
    if (!quickAddForm.deal_name.trim()) return
    createDealMutation.mutate({
      ...quickAddForm,
      pipeline_id: selectedPipelineId,
      stage_id: quickAddStageId,
    })
  }

  // Won/Lost handlers
  const handleMarkWonLost = (deal: Deal, type: 'won' | 'lost') => {
    setWonLostDeal(deal)
    setWonLostType(type)
    setWonLostReason('')
    setWonLostDialogOpen(true)
  }

  const handleWonLostSubmit = () => {
    if (!wonLostDeal) return
    if (wonLostType === 'won') {
      markWonMutation.mutate({ dealId: wonLostDeal.deal_id, reason: wonLostReason })
    } else {
      markLostMutation.mutate({ dealId: wonLostDeal.deal_id, reason: wonLostReason })
    }
  }

  // Group deals by stage
  const dealsByStage = (selectedPipeline?.stages || []).reduce((acc, stage) => {
    acc[stage.stage_id] = deals.filter(d => d.stage_id === stage.stage_id)
    return acc
  }, {} as Record<string, Deal[]>)

  // Calculate stage totals
  const getStageTotals = (stageId: string) => {
    const stageDeals = dealsByStage[stageId] || []
    return {
      count: stageDeals.length,
      value: stageDeals.reduce((sum, d) => sum + (d.deal_value || 0), 0),
    }
  }

  const formatCurrency = (value: number, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold">{t('crm.deals', 'Deals')}</h1>
          <Select value={selectedPipelineId} onValueChange={setSelectedPipelineId}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder={t('crm.selectPipeline', 'Select Pipeline')} />
            </SelectTrigger>
            <SelectContent>
              {pipelines.map((pipeline) => (
                <SelectItem key={pipeline.pipeline_id} value={pipeline.pipeline_id}>
                  {pipeline.pipeline_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Input
            placeholder={t('common.search', 'Search...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-[200px]"
          />
          <Select value={assignedToFilter} onValueChange={setAssignedToFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={t('crm.assignedTo', 'Assigned To')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('common.all', 'All')}</SelectItem>
              {users.map((user) => (
                <SelectItem key={user.user_id} value={user.user_id}>
                  {user.full_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={() => navigate('/crm/pipelines')}>
            <Settings className="h-4 w-4" />
          </Button>
          <Button onClick={() => navigate('/crm/deals/new')}>
            <Plus className="h-4 w-4 mr-2" />
            {t('crm.newDeal', 'New Deal')}
          </Button>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden">
        <div className="flex gap-4 p-4 h-full min-w-max">
          {(selectedPipeline?.stages || [])
            .sort((a, b) => a.display_order - b.display_order)
            .map((stage) => {
              const totals = getStageTotals(stage.stage_id)
              const stageDeals = dealsByStage[stage.stage_id] || []

              return (
                <div
                  key={stage.stage_id}
                  className={`flex-shrink-0 w-80 flex flex-col max-h-full rounded-lg transition-colors ${
                    dragOverStageId === stage.stage_id ? 'bg-primary/10' : 'bg-muted/50'
                  }`}
                  onDragOver={(e) => handleDragOver(e, stage.stage_id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, stage.stage_id)}
                >
                  {/* Stage Header */}
                  <div className="p-3 border-b">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stage.stage_color }} />
                        <span className="font-semibold">{stage.stage_name}</span>
                        <Badge variant="secondary" className="text-xs">{totals.count}</Badge>
                      </div>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleQuickAdd(stage.stage_id)}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {formatCurrency(totals.value)}
                    </div>
                  </div>

                  {/* Deal Cards */}
                  <div className="flex-1 overflow-y-auto p-2 space-y-2">
                    {stageDeals.map((deal) => (
                      <DealCard
                        key={deal.deal_id}
                        deal={deal}
                        t={t}
                        formatCurrency={formatCurrency}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        isDragging={draggedDeal?.deal_id === deal.deal_id}
                        onEdit={() => navigate(`/crm/deals/${deal.deal_id}/edit`)}
                        onDelete={() => deleteDealMutation.mutate(deal.deal_id)}
                        onMarkWon={() => handleMarkWonLost(deal, 'won')}
                        onMarkLost={() => handleMarkWonLost(deal, 'lost')}
                        onClick={() => navigate(`/crm/deals/${deal.deal_id}`)}
                      />
                    ))}
                    {stageDeals.length === 0 && (
                      <div className="text-center text-muted-foreground py-8 text-sm">
                        {t('crm.noDeals', 'No deals')}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
        </div>
      </div>

      {/* Quick Add Dialog */}
      <Dialog open={quickAddDialogOpen} onOpenChange={setQuickAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('crm.quickAddDeal', 'Quick Add Deal')}</DialogTitle>
            <DialogDescription>{t('crm.quickAddDescription', 'Add a new deal to this stage')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="deal_name">{t('crm.dealName', 'Deal Name')}</Label>
              <Input
                id="deal_name"
                value={quickAddForm.deal_name}
                onChange={(e) => setQuickAddForm({ ...quickAddForm, deal_name: e.target.value })}
                placeholder={t('crm.dealNamePlaceholder', 'e.g., Acme Corp - Website Redesign')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="deal_value">{t('crm.dealValue', 'Deal Value')}</Label>
              <Input
                id="deal_value"
                type="number"
                value={quickAddForm.deal_value || ''}
                onChange={(e) => setQuickAddForm({ ...quickAddForm, deal_value: parseFloat(e.target.value) || 0 })}
                placeholder="0"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuickAddDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
            <Button onClick={handleQuickAddSubmit} disabled={!quickAddForm.deal_name.trim()}>
              {t('common.create', 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Won/Lost Dialog */}
      <Dialog open={wonLostDialogOpen} onOpenChange={setWonLostDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {wonLostType === 'won' ? t('crm.markAsWon', 'Mark as Won') : t('crm.markAsLost', 'Mark as Lost')}
            </DialogTitle>
            <DialogDescription>
              {wonLostType === 'won'
                ? t('crm.wonDescription', 'Congratulations! Add a note about why you won this deal.')
                : t('crm.lostDescription', 'Add a note about why this deal was lost.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reason">
                {wonLostType === 'won' ? t('crm.wonReason', 'Won Reason') : t('crm.lostReason', 'Lost Reason')}
              </Label>
              <Textarea
                id="reason"
                value={wonLostReason}
                onChange={(e) => setWonLostReason(e.target.value)}
                placeholder={wonLostType === 'won' ? t('crm.wonReasonPlaceholder', 'What helped close this deal?') : t('crm.lostReasonPlaceholder', 'Why was this deal lost?')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWonLostDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
            <Button
              onClick={handleWonLostSubmit}
              variant={wonLostType === 'won' ? 'default' : 'destructive'}
            >
              {wonLostType === 'won' ? t('crm.markWon', 'Mark Won') : t('crm.markLost', 'Mark Lost')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// Deal Card Component
interface DealCardProps {
  deal: Deal
  t: ReturnType<typeof useTranslation>['t']
  formatCurrency: (value: number, currency?: string) => string
  onDragStart: (e: React.DragEvent, deal: Deal) => void
  onDragEnd: () => void
  isDragging: boolean
  onEdit: () => void
  onDelete: () => void
  onMarkWon: () => void
  onMarkLost: () => void
  onClick: () => void
}

function DealCard({ deal, t, formatCurrency, onDragStart, onDragEnd, isDragging, onEdit, onDelete, onMarkWon, onMarkLost, onClick }: DealCardProps) {
  return (
    <Card
      draggable
      onDragStart={(e) => onDragStart(e, deal)}
      onDragEnd={onDragEnd}
      onClick={onClick}
      className={`cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow ${isDragging ? 'opacity-50' : ''}`}
    >
      <CardContent className="p-3">
        <div className="flex items-start justify-between mb-2">
          <h4 className="font-medium text-sm line-clamp-2">{deal.deal_name}</h4>
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" className="h-6 w-6 -mr-1 -mt-1">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem onClick={onEdit}>
                <Edit2 className="mr-2 h-4 w-4" />
                {t('common.edit', 'Edit')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onMarkWon} className="text-green-600">
                <Trophy className="mr-2 h-4 w-4" />
                {t('crm.markWon', 'Mark Won')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onMarkLost} className="text-orange-600">
                <XCircle className="mr-2 h-4 w-4" />
                {t('crm.markLost', 'Mark Lost')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onDelete} className="text-destructive">
                <Trash2 className="mr-2 h-4 w-4" />
                {t('common.delete', 'Delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="space-y-1.5 text-xs text-muted-foreground">
          {deal.deal_value > 0 && (
            <div className="flex items-center gap-1">
              <DollarSign className="h-3 w-3" />
              <span className="font-medium text-foreground">{formatCurrency(deal.deal_value, deal.currency)}</span>
            </div>
          )}
          {deal.client_name && (
            <div className="flex items-center gap-1">
              <User className="h-3 w-3" />
              <span>{deal.client_name}</span>
            </div>
          )}
          {deal.expected_close_date && (
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              <span>{format(new Date(deal.expected_close_date), 'MMM d, yyyy')}</span>
            </div>
          )}
        </div>

        {deal.status !== 'OPEN' && (
          <Badge
            variant={deal.status === 'WON' ? 'default' : 'destructive'}
            className={`mt-2 text-xs ${deal.status === 'WON' ? 'bg-green-500' : ''}`}
          >
            {deal.status}
          </Badge>
        )}
      </CardContent>
    </Card>
  )
}

